import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import MfaStep from "../components/MfaStep";

export default function Login() {
  const { login, finishLogin } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfa, setMfa] = useState(null); // { pending_token, mfa_enrolled }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const email = `${username.trim().toLowerCase().split("@")[0]}@omnia.os`;
      const res = await login(email, password);
      if (res && res.mfa_required) {
        setMfa(res);
      } else {
        navigate("/os");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const onMfaSuccess = (data) => {
    finishLogin(data);
    navigate("/os");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left — brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between bg-navy text-cream p-14 grain overflow-hidden">
        <Link to="/" className="font-sans text-xl tracking-[0.25em] relative z-10">OMNIA</Link>
        <div className="relative z-10">
          <div className="overline text-cream/40 mb-6">The Neo-Designer Operating System™</div>
          <h1 className="font-editorial text-6xl leading-[0.95]">
            Everything<br />Connected.
          </h1>
          <p className="mt-8 text-cream/60 max-w-sm leading-relaxed">
            {t("auth.brandTagline")}
          </p>
        </div>
        <div className="relative z-10 flex gap-8 text-cream/50 text-sm">
          <span>GOÛT</span><span>VETTA</span><span>AURA</span>
        </div>
        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-gradient-violet opacity-40 blur-3xl" />
      </div>

      {/* Right — form */}
      <div className="flex items-center justify-center p-6 sm:p-14 bg-cream">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden font-sans text-xl tracking-[0.25em] mb-10">OMNIA</div>
          {mfa ? (
            <MfaStep pendingToken={mfa.pending_token} enrolled={mfa.mfa_enrolled} hasPasskeys={mfa.has_passkeys} onSuccess={onMfaSuccess} />
          ) : (
          <>
          <div className="overline text-navy/40 mb-3">{t("auth.welcomeBack")}</div>
          <h2 className="text-3xl font-medium tracking-tight mb-8">{t("auth.signIn")}</h2>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="overline text-navy/50">{t("auth.user")}</label>
              <div className="mt-2 flex items-center bg-white border border-navy/10 rounded-lg px-4 focus-within:border-violet transition-colors">
                <input
                  data-testid="login-email"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoCapitalize="none"
                  className="flex-1 bg-transparent py-3 outline-none"
                  placeholder="leonel"
                />
                <span className="text-navy/40 select-none">@omnia.os</span>
              </div>
            </div>
            <div>
              <label className="overline text-navy/50">{t("auth.password")}</label>
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-2 w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <p data-testid="login-error" className="text-sm text-destructive">{error}</p>
            )}

            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy text-white py-3.5 hover:bg-navy-soft transition-colors disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("auth.enterOs")}
              {!loading && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
            </button>
          </form>

          <p className="mt-6 text-sm text-navy/50">
            <Link to="/forgot-password" data-testid="link-forgot" className="text-violet hover:underline">{t("auth.forgotQ")}</Link>
          </p>
          <p className="mt-2 text-sm text-navy/50">
            <Link to="/forgot-username" data-testid="link-forgot-username" className="text-violet hover:underline">¿Olvidaste tu usuario?</Link>
          </p>
          <p className="mt-2 text-sm text-navy/50">
            {t("auth.privateNote")}{" "}
            <Link to="/register" data-testid="link-register" className="text-violet hover:underline">{t("auth.requestAccess")}</Link>
          </p>
          <p className="mt-2 text-sm text-navy/50">
            {t("auth.createOwnCompanyQ")}{" "}
            <Link to="/register-company" data-testid="link-register-company" className="text-violet hover:underline">{t("auth.registerCompanyLink")}</Link>
          </p>
          </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
